import React, { createContext, useContext, useRef } from 'react';
import { useWebRTCSessionViewModel } from '@/viewmodels/WebRTCSessionViewModel';
import { SessionConnectionStatus } from '@/services/interfaces/Session';
import type { SessionInfo } from '@/services/interfaces/Session';
import type { SessionEvent } from '@/services/interfaces/SessionEvent';
import type { ConversationTurn } from '@/types/backend';
import type { MicrophoneService } from '@/services/implementations/MicrophoneService';

interface WebRTCSessionContextType {
  isSessionActive: boolean;
  sessionConnectionStatus: SessionConnectionStatus;
  sessionInfo: SessionInfo | null;
  rtcEvents: SessionEvent[];
  error: Error | null;
  isSessionServiceInitialized: boolean;
  isStopping: boolean; // 🔑 新增：停止翻译的loading状态
  startTranslation: (microphoneDeviceId: string) => Promise<void>;
  stopTranslation: () => Promise<void>;
  getSessionServiceInstance: () => any;
  clearError: () => void;
  conversationTurns: ConversationTurn[];
  resetConversationData: () => void;
  injectTestEvents: (events: SessionEvent[]) => void;
  clearTestEvents: () => void;
  injectConversationTurns: (turns: ConversationTurn[]) => void;
  clearInjectedConversations: () => void;
}

const WebRTCSessionContext = createContext<WebRTCSessionContextType | undefined>(undefined);

export const WebRTCSessionProvider: React.FC<{ children: React.ReactNode; backendUrl: string; microphoneService: MicrophoneService | null }> = ({ 
  children, 
  backendUrl, 
  microphoneService 
}) => {

  // Add unique ID for tracking
  const hookId = useRef(`VMHook_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
  console.log(`🏗️  WebRTCSessionProvider 初始化了 [${hookId.current}]`);

  const {
    isSessionActive,
    sessionConnectionStatus,
    sessionInfo,
    rtcEvents,
    error,
    isSessionServiceInitialized,
    isStopping,
    startTranslation,
    stopTranslation,
    getSessionServiceInstance,
    clearError,
    conversationTurns,
    resetConversationData,
    injectTestEvents,
    clearTestEvents,
    injectConversationTurns,
    clearInjectedConversations
  } = useWebRTCSessionViewModel(backendUrl, microphoneService, 'webrtc'); //'webrtc' 'websocket'

  // const injectConversationTurns = React.useCallback((turns: ConversationTurn[]) => {
  //   setInjectedConversationTurns(turns);
  //   resetConversationData();
  // }, [setInjectedConversationTurns, resetConversationData]);

  // const clearInjectedConversations = React.useCallback(() => {
  //   setInjectedConversationTurns([]);
  //   resetConversationData();
  // }, [setInjectedConversationTurns, resetConversationData]);

  return (
    <WebRTCSessionContext.Provider value={{
      isSessionActive,
      sessionConnectionStatus,
      sessionInfo,
      rtcEvents,
      error,
      isSessionServiceInitialized,
      isStopping,
      startTranslation,
      stopTranslation,
      getSessionServiceInstance,
      clearError,
      conversationTurns,
      resetConversationData,
      injectTestEvents,
      clearTestEvents,
      injectConversationTurns,
      clearInjectedConversations
    }}>
      {children}
    </WebRTCSessionContext.Provider>
  );
};

export const useWebRTCSession = (): WebRTCSessionContextType => {
  const context = useContext(WebRTCSessionContext);
  if (context === undefined) {
    throw new Error('useWebRTCSession must be used within a WebRTCSessionProvider');
  }
  return context;
}; 